import type { GitStatusResult } from "@cmmd-forge/contracts";
import { ChevronDownIcon, CloudUploadIcon, GitCommitIcon, InfoIcon } from "lucide-react";

import { GitHubIcon } from "./Icons";
import {
  type DefaultBranchActionDialogCopy,
  getMenuActionDisabledReason,
  type GitActionIconName,
  type GitActionMenuItem,
  type GitQuickAction,
  resolveQuickActionIconName,
} from "./GitActionsControl.logic";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Group, GroupSeparator } from "~/components/ui/group";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { ScrollArea } from "~/components/ui/scroll-area";
import { Textarea } from "~/components/ui/textarea";
import { cn } from "~/lib/utils";

const COMMIT_DIALOG_TITLE = "Commit changes";
const COMMIT_DIALOG_DESCRIPTION =
  "Review and confirm your commit. Leave the message blank to auto-generate one.";

type GitChangedFile = GitStatusResult["workingTree"]["files"][number];

export interface GitActionsViewProps {
  readonly compact: boolean;
  readonly gitStatus: GitStatusResult | null;
  readonly gitStatusErrorMessage: string | null;
  readonly hasOriginRemote: boolean;
  readonly isDefaultBranch: boolean;
  readonly isGitActionRunning: boolean;
  readonly isRepo: boolean;
  readonly isInitPending: boolean;
  readonly quickAction: GitQuickAction;
  readonly quickActionDisabledReason: string | null;
  readonly gitActionMenuItems: readonly GitActionMenuItem[];
  readonly allFiles: readonly GitChangedFile[];
  readonly selectedFiles: readonly GitChangedFile[];
  readonly excludedFiles: ReadonlySet<string>;
  readonly allSelected: boolean;
  readonly noneSelected: boolean;
  readonly isEditingFiles: boolean;
  readonly isCommitDialogOpen: boolean;
  readonly dialogCommitMessage: string;
  readonly pendingDefaultBranchActionOpen: boolean;
  readonly pendingDefaultBranchActionCopy: DefaultBranchActionDialogCopy | null;
  readonly onInitializeGit: () => void;
  readonly onRefreshGitStatus: () => void;
  readonly onRunQuickAction: () => void;
  readonly onOpenMenuItem: (item: GitActionMenuItem) => void;
  readonly onCommitDialogOpenChange: (open: boolean) => void;
  readonly onDialogCommitMessageChange: (value: string) => void;
  readonly onToggleEditingFiles: () => void;
  readonly onToggleAllFiles: () => void;
  readonly onToggleFile: (path: string) => void;
  readonly onOpenChangedFile: (path: string) => void;
  readonly onRunDialogActionOnNewBranch: () => void;
  readonly onRunDialogAction: () => void;
  readonly onDefaultBranchDialogOpenChange: (open: boolean) => void;
  readonly onContinuePendingDefaultBranchAction: () => void;
  readonly onCheckoutFeatureBranchAndContinue: () => void;
}

export function GitActionsControlView(props: GitActionsViewProps) {
  if (!props.isRepo) {
    return (
      <Button
        variant="outline"
        size="xs"
        disabled={props.isInitPending}
        onClick={props.onInitializeGit}
      >
        {props.isInitPending ? "Initializing..." : "Initialize Git"}
      </Button>
    );
  }

  return (
    <>
      <GitActionGroup {...props} />
      <GitCommitDialog {...props} />
      <DefaultBranchActionDialog {...props} />
    </>
  );
}

function GitActionItemIcon({ icon }: { readonly icon: GitActionIconName }) {
  if (icon === "commit") return <GitCommitIcon />;
  if (icon === "push") return <CloudUploadIcon />;
  return <GitHubIcon />;
}

function GitQuickActionIcon({ quickAction }: { readonly quickAction: GitQuickAction }) {
  const iconClassName = "size-3.5";
  const iconName = resolveQuickActionIconName(quickAction);
  if (iconName === "commit") return <GitCommitIcon className={iconClassName} />;
  if (iconName === "push") return <CloudUploadIcon className={iconClassName} />;
  if (iconName === "pr") return <GitHubIcon className={iconClassName} />;
  return <InfoIcon className={iconClassName} />;
}

function GitActionGroup(props: GitActionsViewProps) {
  return (
    <Group aria-label="Git actions" className="shrink-0">
      <GitQuickActionButton {...props} />
      <GroupSeparator className={cn("hidden", !props.compact && "@3xl/header-actions:block")} />
      <GitActionMenu {...props} />
    </Group>
  );
}

function GitQuickActionButton(props: GitActionsViewProps) {
  if (props.quickActionDisabledReason) {
    return (
      <Popover>
        <PopoverTrigger
          openOnHover
          render={
            <Button
              aria-disabled="true"
              className="cursor-not-allowed rounded-e-none border-e-0 opacity-64 before:rounded-e-none"
              size="xs"
              variant="outline"
            />
          }
        >
          <GitQuickActionIcon quickAction={props.quickAction} />
          <QuickActionLabel compact={props.compact} label={props.quickAction.label} />
        </PopoverTrigger>
        <PopoverPopup tooltipStyle side="bottom" align="start">
          {props.quickActionDisabledReason}
        </PopoverPopup>
      </Popover>
    );
  }

  return (
    <Button
      variant="outline"
      size="xs"
      disabled={props.isGitActionRunning || props.quickAction.disabled}
      onClick={props.onRunQuickAction}
    >
      <GitQuickActionIcon quickAction={props.quickAction} />
      <QuickActionLabel compact={props.compact} label={props.quickAction.label} />
    </Button>
  );
}

function QuickActionLabel({
  compact,
  label,
}: {
  readonly compact: boolean;
  readonly label: string;
}) {
  return (
    <span
      className={cn(
        "sr-only",
        !compact && "@3xl/header-actions:not-sr-only @3xl/header-actions:ml-0.5",
      )}
    >
      {label}
    </span>
  );
}

function GitActionMenu(props: GitActionsViewProps) {
  return (
    <Menu
      onOpenChange={(open) => {
        if (open) {
          props.onRefreshGitStatus();
        }
      }}
    >
      <MenuTrigger
        render={<Button aria-label="Git action options" size="icon-xs" variant="outline" />}
        disabled={props.isGitActionRunning}
      >
        <ChevronDownIcon aria-hidden="true" className="size-4" />
      </MenuTrigger>
      <MenuPopup align="end" className="w-full">
        {props.gitActionMenuItems.map((item) => (
          <GitActionMenuItemView key={`${item.id}-${item.label}`} item={item} {...props} />
        ))}
        <GitActionMenuHints {...props} />
      </MenuPopup>
    </Menu>
  );
}

function GitActionMenuItemView(props: GitActionsViewProps & { readonly item: GitActionMenuItem }) {
  const disabledReason = getMenuActionDisabledReason({
    item: props.item,
    gitStatus: props.gitStatus,
    isBusy: props.isGitActionRunning,
    hasOriginRemote: props.hasOriginRemote,
  });

  if (props.item.disabled && disabledReason) {
    return (
      <Popover>
        <PopoverTrigger
          openOnHover
          nativeButton={false}
          render={<span className="block w-max cursor-not-allowed" />}
        >
          <MenuItem className="w-full" disabled>
            <GitActionItemIcon icon={props.item.icon} />
            {props.item.label}
          </MenuItem>
        </PopoverTrigger>
        <PopoverPopup tooltipStyle side="left" align="center">
          {disabledReason}
        </PopoverPopup>
      </Popover>
    );
  }

  return (
    <MenuItem
      disabled={props.item.disabled}
      onClick={() => {
        props.onOpenMenuItem(props.item);
      }}
    >
      <GitActionItemIcon icon={props.item.icon} />
      {props.item.label}
    </MenuItem>
  );
}

function GitActionMenuHints(props: GitActionsViewProps) {
  const pushItem = props.gitActionMenuItems.find((item) => item.id === "push");
  const pullRequestItem = props.gitActionMenuItems.find((item) => item.id === "pr");
  const pushReason = getMenuActionDisabledReason({
    item: pushItem ?? {
      id: "push",
      label: "Push",
      disabled: false,
      icon: "push",
      kind: "open_dialog",
    },
    gitStatus: props.gitStatus,
    isBusy: props.isGitActionRunning,
    hasOriginRemote: props.hasOriginRemote,
  });
  const pullRequestReason = getMenuActionDisabledReason({
    item: pullRequestItem ?? {
      id: "pr",
      label: "Create PR",
      disabled: false,
      icon: "pr",
      kind: "open_dialog",
    },
    gitStatus: props.gitStatus,
    isBusy: props.isGitActionRunning,
    hasOriginRemote: props.hasOriginRemote,
  });
  return (
    <>
      {props.gitStatus?.branch === null ? (
        <p className="px-2 py-1.5 text-xs text-warning">
          Detached HEAD: create and checkout a branch to enable push and PR actions.
        </p>
      ) : null}
      {props.gitStatus &&
      props.gitStatus.branch !== null &&
      !props.gitStatus.hasWorkingTreeChanges &&
      props.gitStatus.behindCount > 0 &&
      props.gitStatus.aheadCount === 0 ? (
        <p className="px-2 py-1.5 text-xs text-warning">Behind upstream. Pull/rebase first.</p>
      ) : null}
      {props.gitStatusErrorMessage ? (
        <p className="px-2 py-1.5 text-xs text-destructive">{props.gitStatusErrorMessage}</p>
      ) : null}
      {pushReason ? (
        <p className="px-2 py-1.5 text-xs text-muted-foreground">Push: {pushReason}</p>
      ) : null}
      {pullRequestReason ? (
        <p className="px-2 py-1.5 text-xs text-muted-foreground">
          Pull request: {pullRequestReason}
        </p>
      ) : null}
    </>
  );
}

function GitCommitDialog(props: GitActionsViewProps) {
  return (
    <Dialog open={props.isCommitDialogOpen} onOpenChange={props.onCommitDialogOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>{COMMIT_DIALOG_TITLE}</DialogTitle>
          <DialogDescription>{COMMIT_DIALOG_DESCRIPTION}</DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          <GitCommitDialogFileSummary {...props} />
          <div className="space-y-1">
            <p className="text-xs font-medium">Commit message (optional)</p>
            <Textarea
              value={props.dialogCommitMessage}
              onChange={(event) => props.onDialogCommitMessageChange(event.target.value)}
              placeholder="Leave empty to auto-generate"
              size="sm"
            />
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => props.onCommitDialogOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={props.noneSelected}
            onClick={props.onRunDialogActionOnNewBranch}
          >
            Commit on new branch
          </Button>
          <Button size="sm" disabled={props.noneSelected} onClick={props.onRunDialogAction}>
            Commit
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function GitCommitDialogFileSummary(props: GitActionsViewProps) {
  return (
    <div className="space-y-3 rounded-lg border border-input bg-muted/40 p-3 text-xs">
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1">
        <span className="text-muted-foreground">Branch</span>
        <span className="flex items-center justify-between gap-2">
          <span className="font-medium">{props.gitStatus?.branch ?? "(detached HEAD)"}</span>
          {props.isDefaultBranch ? (
            <span className="text-right text-warning text-xs">Warning: default branch</span>
          ) : null}
        </span>
      </div>
      <div className="space-y-1">
        <GitCommitFileHeader {...props} />
        {!props.gitStatus || props.allFiles.length === 0 ? (
          <p className="font-medium">none</p>
        ) : (
          <GitCommitFileList {...props} />
        )}
      </div>
    </div>
  );
}

function GitCommitFileHeader(props: GitActionsViewProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {props.isEditingFiles && props.allFiles.length > 0 ? (
          <Checkbox
            checked={props.allSelected}
            indeterminate={!props.allSelected && !props.noneSelected}
            onCheckedChange={props.onToggleAllFiles}
          />
        ) : null}
        <span className="text-muted-foreground">Files</span>
        {!props.allSelected && !props.isEditingFiles ? (
          <span className="text-muted-foreground">
            ({props.selectedFiles.length} of {props.allFiles.length})
          </span>
        ) : null}
      </div>
      {props.allFiles.length > 0 ? (
        <Button variant="ghost" size="xs" onClick={props.onToggleEditingFiles}>
          {props.isEditingFiles ? "Done" : "Edit"}
        </Button>
      ) : null}
    </div>
  );
}

function GitCommitFileList(props: GitActionsViewProps) {
  const insertions = props.selectedFiles.reduce((sum, file) => sum + file.insertions, 0);
  const deletions = props.selectedFiles.reduce((sum, file) => sum + file.deletions, 0);

  return (
    <div className="space-y-2">
      <ScrollArea className="h-44 rounded-md border border-input bg-background">
        <div className="space-y-1 p-1">
          {props.allFiles.map((file) => (
            <GitCommitFileRow key={file.path} file={file} {...props} />
          ))}
        </div>
      </ScrollArea>
      <div className="flex justify-end font-mono">
        <span className="text-success">+{insertions}</span>
        <span className="text-muted-foreground"> / </span>
        <span className="text-destructive">-{deletions}</span>
      </div>
    </div>
  );
}

function GitCommitFileRow(props: GitActionsViewProps & { readonly file: GitChangedFile }) {
  const isExcluded = props.excludedFiles.has(props.file.path);
  return (
    <div className="flex w-full items-center gap-2 rounded-md px-2 py-1 font-mono text-xs transition-colors hover:bg-accent/50">
      {props.isEditingFiles ? (
        <Checkbox
          checked={!isExcluded}
          onCheckedChange={() => props.onToggleFile(props.file.path)}
        />
      ) : null}
      <button
        type="button"
        className="flex flex-1 items-center justify-between gap-3 text-left truncate"
        onClick={() => props.onOpenChangedFile(props.file.path)}
      >
        <span className={`truncate${isExcluded ? " text-muted-foreground" : ""}`}>
          {props.file.path}
        </span>
        <span className="shrink-0">
          {isExcluded ? (
            <span className="text-muted-foreground">Excluded</span>
          ) : (
            <>
              <span className="text-success">+{props.file.insertions}</span>
              <span className="text-muted-foreground"> / </span>
              <span className="text-destructive">-{props.file.deletions}</span>
            </>
          )}
        </span>
      </button>
    </div>
  );
}

function DefaultBranchActionDialog(props: GitActionsViewProps) {
  return (
    <Dialog
      open={props.pendingDefaultBranchActionOpen}
      onOpenChange={props.onDefaultBranchDialogOpenChange}
    >
      <DialogPopup className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {props.pendingDefaultBranchActionCopy?.title ?? "Run action on default branch?"}
          </DialogTitle>
          <DialogDescription>{props.pendingDefaultBranchActionCopy?.description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => props.onDefaultBranchDialogOpenChange(false)}
          >
            Abort
          </Button>
          <Button variant="outline" size="sm" onClick={props.onContinuePendingDefaultBranchAction}>
            {props.pendingDefaultBranchActionCopy?.continueLabel ?? "Continue"}
          </Button>
          <Button size="sm" onClick={props.onCheckoutFeatureBranchAndContinue}>
            Checkout feature branch & continue
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
